import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import {
  makeDiscussAutoRuleId,
  normalizeDiscussAutoRule,
  readDiscussAutoRulesFromPreferences,
} from '@/shein-platform/modules/discuss/discussAutoRules';
import { showToast } from '@/utils/toast';

function formatPercent(value) {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '—';
}

/** 三档文案：低于拒绝 / 中间人工 / 不低于同意 */
function discussAutoRuleBands(rejectPercent, acceptPercent) {
  const lo = formatPercent(rejectPercent);
  const hi = formatPercent(acceptPercent);
  return {
    reject: `<${lo}% 拒绝`,
    manual: `${lo}%～${hi}% 人工`,
    accept: `≥${hi}% 同意`,
  };
}

/**
 * 议价自动规则：按类目配置「建议价相对报价」的自动拒绝 / 自动同意阈值。
 */
export default function DiscussAutoRuleModal({
  open,
  shopId,
  busy = false,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftCategoryLabel, setDraftCategoryLabel] = useState('');
  const [draftProductTypeId, setDraftProductTypeId] = useState('');
  const [draftReject, setDraftReject] = useState('50');
  const [draftAccept, setDraftAccept] = useState('90');

  const leafOptions = useMemo(() => flattenLeafCategories(categories), [categories]);

  const resetDraft = useCallback(() => {
    setDraftCategoryId('');
    setDraftCategoryLabel('');
    setDraftProductTypeId('');
    setDraftReject('50');
    setDraftAccept('90');
  }, []);

  useEffect(() => {
    if (!open) return;
    resetDraft();
  }, [open, shopId, resetDraft]);

  useEffect(() => {
    if (!open || !shopId) {
      setCategories([]);
      setRules([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setLoading(true);
    Promise.all([
      sheinPlatformAPI.getShopCategories(shopId).catch(() => null),
      sheinPlatformAPI.getPreferences().catch(() => null),
    ])
      .then(([catRes, prefRes]) => {
        if (cancelled) return;
        setCategories(catRes?.categories ?? catRes?.items ?? catRes?.list ?? catRes ?? []);
        const prefs = prefRes?.preferences || prefRes || {};
        setRules(readDiscussAutoRulesFromPreferences(prefs, shopId));
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [open, shopId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, busy, onClose]);

  const onCategoryChange = (value) => {
    const id = String(value || '').trim();
    setDraftCategoryId(id);
    if (id === '*') {
      setDraftCategoryLabel('全部类目');
      setDraftProductTypeId('');
      return;
    }
    const opt = leafOptions.find((o) => String(o.categoryId) === id);
    setDraftCategoryLabel(opt?.label || '');
    setDraftProductTypeId(opt?.productTypeId || '');
  };

  const addRule = () => {
    if (!draftCategoryId) {
      showToast(fixedUi("请选择类目"), 'warning');
      return;
    }
    const rejectBelowPercent = Number(draftReject);
    const acceptAbovePercent = Number(draftAccept);
    if (!Number.isFinite(rejectBelowPercent) || rejectBelowPercent < 0 || rejectBelowPercent > 100) {
      showToast(fixedUi("自动拒绝阈值请填写 0～100 的数字"), 'error');
      return;
    }
    if (!Number.isFinite(acceptAbovePercent) || acceptAbovePercent < 0 || acceptAbovePercent > 100) {
      showToast(fixedUi("自动同意阈值请填写 0～100 的数字"), 'error');
      return;
    }
    if (rejectBelowPercent >= acceptAbovePercent) {
      showToast(fixedUi("拒绝阈值须小于同意阈值，中间留给人工处理"), 'error');
      return;
    }
    if (rules.some((r) => String(r.categoryId) === String(draftCategoryId))) {
      showToast(fixedUi("该类目已有规则，请先删除后再添加"), 'warning');
      return;
    }
    const next = normalizeDiscussAutoRule({
      id: makeDiscussAutoRuleId(),
      enabled: true,
      categoryId: draftCategoryId,
      categoryPathLabel: draftCategoryLabel || (draftCategoryId === '*' ? '全部类目' : draftCategoryId),
      productTypeId: draftProductTypeId,
      rejectBelowPercent,
      acceptAbovePercent,
    });
    if (!next) {
      showToast(fixedUi("规则无效"), 'error');
      return;
    }
    setRules((prev) => [...prev, next]);
    resetDraft();
    showToast(fixedUi("已加入规则列表，请点击保存生效"), 'info');
  };

  const removeRule = (id) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const toggleRule = (id, enabled) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled } : r)));
  };

  const handleSave = async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    setSaving(true);
    try {
      // 先读再写：mergePreferences 对嵌套对象是整 key 覆盖，需带上其它店铺规则
      const prefRes = await sheinPlatformAPI.getPreferences();
      const prefs = prefRes?.preferences || prefRes || {};
      const existingByShop = prefs?.discussAutoRulesByShop && typeof prefs.discussAutoRulesByShop === 'object'
        ? { ...prefs.discussAutoRulesByShop }
        : {};
      existingByShop[String(shopId)] = rules.map(normalizeDiscussAutoRule).filter(Boolean);
      await sheinPlatformAPI.updatePreferences({
        activeShopId: Number(shopId) || undefined,
        preferences: {
          discussAutoRulesByShop: existingByShop,
        },
      });
      showToast(fixedUi("自动规则已保存"), 'success');
      onSaved?.(rules);
      onClose?.();
    } catch (e) {
      showToast(e?.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const disabled = busy || saving || loading;
  const draftBands = discussAutoRuleBands(draftReject, draftAccept);

  return createPortal(
    <div className="spo-batch-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={disabled}
        onClick={() => !disabled && onClose?.()}
      />
      <div
        className="spo-batch-modal__panel spo-discuss-auto-rule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-discuss-auto-rule-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-discuss-auto-rule-title">{fixedUi("议价自动规则")}</h2>
          <button
            type="button"
            className="spo-btn spo-btn--ghost spo-btn--sm"
            disabled={disabled}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>

        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">
            建议价 ÷ 商家最新报价。例：报价 20、建议 10 → 50%。
          </p>
          <div className="spo-discuss-auto-rule-bands">
            <span className="spo-discuss-auto-rule-bands__seg spo-discuss-auto-rule-bands__seg--reject">{draftBands.reject}</span>
            <span className="spo-discuss-auto-rule-bands__seg spo-discuss-auto-rule-bands__seg--manual">{draftBands.manual}</span>
            <span className="spo-discuss-auto-rule-bands__seg spo-discuss-auto-rule-bands__seg--accept">{draftBands.accept}</span>
          </div>

          <div className="spo-discuss-auto-rule-form">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{fixedUi("适用类目")}</span>
              <CategoryCascaderSelect
                categories={categories}
                value={draftCategoryId}
                label={draftCategoryLabel}
                disabled={disabled || metaLoading || !shopId}
                placeholder={metaLoading ? '类目加载中…' : fixedUi("请选择或搜索类目")}
                emptyText={!shopId ? fixedUi("请先选择店铺") : fixedUi("暂无可发商品分类")}
                includeAllOption
                onChange={onCategoryChange}
              />
            </label>
            <div className="spo-discuss-auto-rule-form__row">
              <label className="spo-field">
                <span className="spo-field__label">{fixedUi("低于 % 拒绝")}</span>
                <input
                  className="spo-input"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={draftReject}
                  disabled={disabled}
                  onChange={(e) => setDraftReject(e.target.value)}
                />
              </label>
              <label className="spo-field">
                <span className="spo-field__label">{fixedUi("不低于 % 同意")}</span>
                <input
                  className="spo-input"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={draftAccept}
                  disabled={disabled}
                  onChange={(e) => setDraftAccept(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="spo-btn spo-btn--secondary spo-btn--sm spo-discuss-auto-rule-form__add"
                disabled={disabled}
                onClick={addRule}
              >
                添加
              </button>
            </div>
          </div>

          <div className="spo-discuss-auto-rule-list">
            <div className="spo-discuss-auto-rule-list__head">{fixedUi('已配置规则')}（{rules.length}）</div>
            {loading ? (
              <p className="spo-batch-modal__hint">{fixedUi("加载中…")}</p>
            ) : rules.length === 0 ? (
              <p className="spo-batch-modal__hint">{fixedUi("暂无规则，选类目并填写阈值后添加。")}</p>
            ) : (
              <ul className="spo-discuss-auto-rule-list__ul">
                {rules.map((rule) => {
                  const bands = discussAutoRuleBands(rule.rejectBelowPercent, rule.acceptAbovePercent);
                  const catLabel = rule.categoryPathLabel || rule.categoryId;
                  return (
                    <li key={rule.id} className="spo-discuss-auto-rule-list__item">
                      <div className="spo-discuss-auto-rule-list__main">
                        <span className="spo-discuss-auto-rule-list__cat" title={catLabel}>{catLabel}</span>
                        <span>{bands.reject} · {bands.manual} · {bands.accept}</span>
                      </div>
                      <div className="spo-discuss-auto-rule-list__ops">
                        <SheinSwitch
                          checked={rule.enabled !== false}
                          disabled={disabled}
                          ariaLabel={`${catLabel || '规则'}启用`}
                          onChange={(next) => toggleRule(rule.id, next)}
                        />
                        <button
                          type="button"
                          className="spo-btn spo-btn--ghost spo-btn--sm"
                          disabled={disabled}
                          onClick={() => removeRule(rule.id)}
                        >
                          {fixedUi("删除")}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="spo-batch-modal__foot">
          <button
            type="button"
            className="spo-btn spo-btn--secondary"
            disabled={disabled}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary"
            disabled={disabled || !shopId}
            onClick={handleSave}
          >
            {saving ? fixedUi("保存中…") : fixedUi("保存")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
